import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Target, RotateCcw, AlertTriangle } from 'lucide-react';
import { Button, ProgressBar, Modal } from '../components';
import { useProgress } from '../hooks/useProgress';
import challengesData from '../data/challenges.json';

export default function Progress() {
  const {
    progress,
    resetProgress,
    xpPerLevel,
    achievements: achievementDefinitions,
  } = useProgress();
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleReset = () => {
    resetProgress();
    setShowResetConfirm(false);
  };

  // Calculate total challenges
  const totalChallenges = Object.values(challengesData.challenges).reduce(
    (sum, challenges) => sum + challenges.length,
    0
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="p-4 border-b border-game-secondary">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-game-text">Your Progress</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowResetConfirm(true)}
            className="text-game-accent hover:text-red-400"
          >
            <RotateCcw size={16} className="mr-2" />
            Reset
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Stats */}
          <div className="lg:col-span-1 space-y-6">
            {/* Level card */}
            <div className="p-6 bg-game-surface rounded-xl border border-game-secondary">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-game-accent/20 flex items-center justify-center">
                  <span className="text-2xl font-bold text-game-accent">
                    {progress?.level || 1}
                  </span>
                </div>
                <div>
                  <p className="text-game-text-dim text-sm">Current Level</p>
                  <p className="text-2xl font-bold text-game-text">
                    Level {progress?.level || 1}
                  </p>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-game-text-dim">XP Progress</span>
                  <span className="text-game-text font-medium">
                    {progress?.current_xp || 0} / {xpPerLevel}
                  </span>
                </div>
                <ProgressBar
                  value={progress?.current_xp || 0}
                  max={xpPerLevel}
                  showLabel={false}
                  height="h-3"
                />
              </div>
              <div className="mt-4 pt-4 border-t border-game-secondary">
                <p className="text-sm text-game-text-dim">
                  Total XP: <span className="text-game-accent font-medium">{progress?.total_xp || 0}</span>
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="p-4 bg-game-surface rounded-lg border border-game-secondary text-center">
              <Target className="mx-auto mb-2 text-game-accent" size={24} />
              <p className="text-2xl font-bold text-game-text">
                {progress?.challenges_completed || 0}
              </p>
              <p className="text-xs text-game-text-dim">Challenges Completed</p>
            </div>

            {/* Achievements */}
            <div className="p-4 bg-game-surface rounded-xl border border-game-secondary">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="text-game-warning" size={20} />
                <h2 className="font-bold text-game-text">Achievements</h2>
                <span className="text-sm text-game-text-dim">
                  ({progress?.achievements?.length || 0}/{achievementDefinitions.length})
                </span>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-contrast">
                {achievementDefinitions.map((achievement) => {
                  const isUnlocked = progress?.achievements?.includes(achievement.name);
                  return (
                    <div
                      key={achievement.id}
                      className={`p-3 rounded-lg ${
                        isUnlocked
                          ? 'bg-game-warning/10 border border-game-warning/30'
                          : 'bg-game-secondary/30 opacity-50'
                      }`}
                    >
                      <p className={`font-medium text-sm ${isUnlocked ? 'text-game-warning' : 'text-game-text-dim'}`}>
                        {achievement.name}
                      </p>
                      <p className="text-xs text-game-text-dim">{achievement.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right column: Topic progress */}
          <div className="lg:col-span-2">
            <div className="p-4 bg-game-surface rounded-xl border border-game-secondary">
              <h2 className="font-bold text-game-text mb-4">Topics Progress</h2>
              <div className="space-y-4">
                {challengesData.topics.map((topic, index) => {
                  const topicChallenges = challengesData.challenges[topic.id] || [];
                  const topicProgress = progress?.worlds?.[topic.id] || {};
                  const completedCount = topicProgress.completed?.length || 0;
                  const completion = topicProgress.completion || 0;

                  // First topic is always unlocked, others require previous topic at 50%+
                  const prevTopic = index > 0 ? challengesData.topics[index - 1] : null;
                  const prevTopicProgress = prevTopic ? progress?.worlds?.[prevTopic.id] : null;
                  const isLocked = prevTopicProgress && prevTopicProgress.completion < 50;

                  return (
                    <div
                      key={topic.id}
                      className={`block p-4 rounded-lg bg-game-secondary/30 ${isLocked ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={`font-medium ${isLocked ? 'text-game-text-dim' : 'text-game-text'}`}>
                          {topic.name}
                        </h3>
                        <span className={`text-sm font-medium ${isLocked ? 'text-game-text-dim' : completion === 100 ? 'text-game-accent' : 'text-game-text'}`}>
                          {isLocked ? 'Locked' : `${completion}%`}
                        </span>
                      </div>
                      <ProgressBar
                        value={isLocked ? 0 : completion}
                        max={100}
                        showLabel={false}
                        height="h-2"
                        color={completion === 100 ? 'bg-game-success' : 'bg-game-primary'}
                      />
                      <div className="mt-2 text-xs text-game-text-dim">
                        <span>
                          {isLocked
                            ? `Complete 50% of ${prevTopic?.name} to unlock`
                            : `${completedCount} / ${topicChallenges.length} challenges`
                          }
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Overall progress */}
              <div className="mt-6 pt-4 border-t border-game-secondary">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-game-text">Overall Progress</span>
                  <span className="text-game-text font-medium">
                    {Math.round((progress?.challenges_completed || 0) / totalChallenges * 100)}%
                  </span>
                </div>
                <ProgressBar
                  value={progress?.challenges_completed || 0}
                  max={totalChallenges}
                  height="h-3"
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Reset confirmation modal */}
      <Modal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title="Reset Progress?"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-game-warning flex-shrink-0" size={24} />
            <p className="text-game-text-dim">
              This will delete all your progress, including completed challenges, achievements, and XP.
              This action cannot be undone.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setShowResetConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReset}>
              Reset Everything
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
